'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@planner/ui'
import { useMenuCategories } from '../hooks/useMenuCategories'
import { useMenuItemCategories } from '../hooks/useMenuItemCategories'
import type { MenuItem } from '@planner/types'

interface MenuItemFormProps {
  item?: MenuItem | null
  onClose: () => void
}

export function MenuItemForm({ item, onClose }: MenuItemFormProps) {
  const { categories, loading: categoriesLoading } = useMenuCategories()
  const { getCategoriesForItem } = useMenuItemCategories()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    selectedCategoryIds: [] as string[],
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingCategories, setLoadingCategories] = useState(false)

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description || '',
        price: item.price.toString(),
        selectedCategoryIds: [],
      })
      
      // Załaduj istniejące kategorie dania
      setLoadingCategories(true)
      getCategoriesForItem(item.id).then((itemCategories) => {
        setFormData(prev => ({
          ...prev,
          selectedCategoryIds: itemCategories.map(cat => cat.id),
        }))
        setLoadingCategories(false)
      }).catch(() => {
        setLoadingCategories(false)
      })
    }
  }, [item, getCategoriesForItem])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    // Walidacja: minimum 1 kategoria
    if (formData.selectedCategoryIds.length === 0) {
      setError('Musisz wybrać co najmniej jedną kategorię')
      setLoading(false)
      return
    }

    try {
      const menuItemData = {
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
      }

      let menuItemId: string

      if (item) {
        // Edycja: zaktualizuj danie
        const { error: updateError } = await supabase
          .from('menu_items')
          .update(menuItemData as unknown as never)
          .eq('id', item.id)

        if (updateError) throw updateError
        menuItemId = item.id

        // Usuń wszystkie istniejące relacje kategorii
        const { error: deleteError } = await supabase
          .from('menu_item_categories')
          .delete()
          .eq('menu_item_id', menuItemId)

        if (deleteError) throw deleteError
      } else {
        // Nowe danie: utwórz danie
        const { data: newItem, error: insertError } = await supabase
          .from('menu_items')
          .insert([menuItemData] as unknown as never)
          .select()
          .single()

        if (insertError) throw insertError
        if (!newItem) throw new Error('Nie udało się utworzyć dania')
        menuItemId = (newItem as MenuItem).id
      }

      // Dodaj nowe relacje kategorii
      if (formData.selectedCategoryIds.length > 0) {
        const categoryRelations = formData.selectedCategoryIds.map(categoryId => ({
          menu_item_id: menuItemId,
          menu_category_id: categoryId,
        }))

        const { error: insertCategoriesError } = await supabase
          .from('menu_item_categories')
          .insert(categoryRelations as unknown as never)

        if (insertCategoriesError) throw insertCategoriesError
      }

      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryToggle = (categoryId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedCategoryIds.includes(categoryId)
      if (isSelected) {
        return {
          ...prev,
          selectedCategoryIds: prev.selectedCategoryIds.filter(id => id !== categoryId),
        }
      } else {
        return {
          ...prev,
          selectedCategoryIds: [...prev.selectedCategoryIds, categoryId],
        }
      }
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{item ? 'Edytuj danie' : 'Dodaj danie'}</CardTitle>
        <CardDescription>
          {item ? 'Edytuj szczegóły dania' : 'Dodaj nowe danie do menu'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Nazwa *
              </label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Kategorie *</Label>
              {categoriesLoading || loadingCategories ? (
                <div className="text-sm text-muted-foreground">Ładowanie kategorii...</div>
              ) : categories.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Brak kategorii. Dodaj kategorie w zakładce &quot;Kategorie&quot;.
                </div>
              ) : (
                <div className="space-y-2 border rounded-md p-4 max-h-60 overflow-y-auto">
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedCategoryIds.includes(category.id)}
                        onChange={() => handleCategoryToggle(category.id)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm">
                        {category.name}
                        {category.description && (
                          <span className="text-muted-foreground ml-2">- {category.description}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {formData.selectedCategoryIds.length === 0 && !categoriesLoading && !loadingCategories && (
                <p className="text-xs text-destructive">Musisz wybrać co najmniej jedną kategorię</p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="price" className="text-sm font-medium">
                Cena (zł) *
              </label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                Opis
              </label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 p-4 rounded-md">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          </form>
        </CardContent>
    </Card>
  )
}

